import React, { useEffect, useState, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import SendIcon from "@mui/icons-material/Send";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import api from "../../../src/utils/api";
import { Link } from "react-router-dom";
import { exportRequestsToCSV } from "./exportCsv";
import { PermissionsContext } from "../../../src/utils/PermissionsContext";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Paper,
  Stack,
} from "@mui/material";

const Integrations = () => {
  const [requests, setRequests] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [viewMode, setViewMode] = useState("cards");
  const username = localStorage.getItem("username");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportSummary, setExportSummary] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportedRecords, setExportedRecords] = useState([]);
  const [members, setMembers] = useState([]);
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);
  const [hoursMap, setHoursMap] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [activeTab, setActiveTab] = useState({});

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
    overflow: "hidden",
  };

  const handleTabChange = (id, newValue) => {
    setActiveTab((prev) => ({ ...prev, [id]: newValue }));
    if (newValue === "comments") {
      const workOrderId = requests.find((r) => r.id === id)?.work_order_id;
      if (workOrderId) fetchComments(workOrderId);
    }
  };

  useEffect(() => {
    fetchBillableRequests();
    fetchWorkOrderTypes();
    fetchMembers();
  }, []);

  const fetchBillableRequests = async () => {
    try {
      const res = await api.get("/billable-requests");
      const normalized = res.data.map((r) => ({
        ...r,
        price: r.price ?? 0,
        labor_price: r.labor_price ?? 0,
        hours: r.hours ?? 0,
      }));
      setRequests(normalized);
    } catch (err) {
      console.error("Failed to fetch billable requests:", err);
    }
  };

  const fetchComments = async (work_order_id) => {
    if (!work_order_id) return;
    setLoadingComments((prev) => ({ ...prev, [work_order_id]: true }));
    try {
      const res = await api.get(`/getComments/${work_order_id}`);
      setCommentsMap((prev) => ({ ...prev, [work_order_id]: res.data }));
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    } finally {
      setLoadingComments((prev) => ({ ...prev, [work_order_id]: false }));
    }
  };

  useEffect(() => {
    if (requests.length) {
      const initialHours = {};
      requests.forEach((r) => {
        initialHours[r.id] = 0;
      });
      setHoursMap(initialHours);
    }
  }, [requests]);

  useEffect(() => {
    if (requests.length) {
      const defaults = {};
      requests.forEach((r) => {
        defaults[r.id] = "details";
      });
      setActiveTab(defaults);
    }
  }, [requests]);

  const fetchMembers = async () => {
    try {
      const res = await api.get("/members?search=");
      setMembers(res.data.members);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
  };

  const fetchWorkOrderTypes = async () => {
    try {
      const res = await api.get("/workorder-types");
      setWorkOrderTypes(res.data);
    } catch (err) {
      console.error("Failed to fetch work order types:", err);
    }
  };

  const getMemberInfo = (member_number) => {
    if (!member_number) return null;
    const formatted = String(member_number).padStart(6, "0");
    return members.find((m) => m.ClubMemberCode === formatted);
  };

  const handleSendToJonas = async (requestId) => {
    const req = requests.find((r) => r.id === requestId);
    const sentBy = localStorage.getItem("username") || "unknown_user";
    const member = getMemberInfo(req.member_number);
    const memberTypeCode = member?.ClubMembershipTypeCode || null;

    try {
      await api.post("/jonas-exports", {
        billable_request_id: requestId,
        sent_by: sentBy,
        member_type_code: memberTypeCode,
      });

      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      console.log(`Request ${requestId} sent to Jonas`);
    } catch (err) {
      console.error("Failed to send to Jonas:", err);
      alert("Failed to send request to Jonas. Check console for details.");
    }
  };

  const handleHoursChange = (id, hoursStr) => {
    const hours = hoursStr === "" ? "" : parseFloat(hoursStr) || 0;

    setHoursMap((prev) => ({ ...prev, [id]: hours }));

    if (hours !== "" && !isNaN(hours)) {
      setRequests((prev) =>
        prev.map((r) =>
          r.id === id && ["1", "2"].includes(String(r.work_order_type))
            ? { ...r, hours, labor_price: parseFloat((hours * 43).toFixed(2)) }
            : r
        )
      );
    }
  };

  const handlePriceChange = (id, value, field) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, [field]: parseFloat(value) || 0 } : r
      )
    );
  };

  const savePrice = async (req) => {
    try {
      await api.put(`/billable-requests/${req.id}`, {
        ...req,
        price: req.price,
        labor_price: req.labor_price || 0,
      });
    } catch (err) {
      console.error("Failed to update price:", err);
    }
  };

  const handleSendAll = async () => {
    const billableRequests = requests.filter((r) => r.billable);
    const sentBy = username || "unknown_user";
    const detailedResults = [];

    for (const req of billableRequests) {
      try {
        const member = getMemberInfo(req.member_number);
        const memberTypeCode = member?.ClubMembershipTypeCode || null;

        await api.post("/jonas-exports", {
          billable_request_id: req.id,
          sent_by: sentBy,
          member_type_code: memberTypeCode,
        });

        if (req.price > 0) {
          detailedResults.push({
            member_number: req.member_number,
            invoice_date: req.closed_at,
            chit_number: req.work_order_id?.toString().padStart(6, "0") || "—",
            item_group: req.work_order_type,
            export_description: `${req.description} - MATERIAL`,
            total: parseFloat(req.price).toFixed(2),
          });
        }

        if (req.labor_price > 0) {
          detailedResults.push({
            member_number: req.member_number,
            invoice_date: req.closed_at,
            chit_number: req.work_order_id?.toString().padStart(6, "0") || "—",
            item_group: req.work_order_type,
            export_description: `${req.description} - LABOR`,
            total: parseFloat(req.labor_price).toFixed(2),
          });
        }
      } catch (err) {
        console.error(`Failed to export ID ${req.id}`, err);
      }
    }

    await fetchBillableRequests();
    setExportedRecords(detailedResults);
    setExportModalOpen(true);
    setConfirmOpen(false);
  };

  const handleRowUpdate = async (updatedRow) => {
    try {
      await api.put(`/billable-requests/${updatedRow.id}`, updatedRow);
      return updatedRow;
    } catch (err) {
      console.error("Error updating row:", err);
      throw err;
    }
  };

  const getRequestTypeLabel = (req) => {
    if (req.work_order_id) return `Work Order ID: ${req.work_order_id}`;
    if (req.violation_id) return `Violation ID: ${req.violation_id}`;
    if (req.architectural_request_id)
      return `Architectural Request ID: ${req.architectural_request_id}`;
    if (req.general_request_id)
      return `General Request ID: ${req.general_request_id}`;
    return "Request Type: Unknown";
  };

  const getTypeName = (req) => {
    if (req.work_order_id && req.work_order_type) {
      const match = workOrderTypes.find(
        (w) => String(w.id) === String(req.work_order_type)
      );
      return match ? `Work Order Type: ${match.type}` : "Work Order Type: Unknown";
    }
    if (req.violation_id && req.violation_type_name)
      return `Violation Type: ${req.violation_type_name}`;
    if (req.architectural_request_id && req.architectural_type_name)
      return `Architectural Type: ${req.architectural_type_name}`;
    return "Type: Unknown";
  };

  const columns = [
    { field: "title", headerName: "Title", width: 200 },
    { field: "description", headerName: "Description", width: 250 },
    {
      field: "type",
      headerName: "Type",
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2">{getTypeName(params.row)}</Typography>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      width: 130,
      editable: true,
      type: "number",
      valueFormatter: (params) =>
        params.value != null
          ? `$${parseFloat(params.value).toFixed(2)}`
          : "$0.00",
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      renderCell: (params) =>
        params.row.billable ? (
          <Button
            variant="outlined"
            size="small"
            endIcon={<SendIcon />}
            onClick={() => handleSendToJonas(params.row.id)}
            sx={secondaryButtonSx}
          >
            Send to Jonas
          </Button>
        ) : null,
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={pageCardSx}>
        <Box sx={{ height: 5, backgroundColor: "#2f63ad" }} />

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 3,
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                Billable Requests
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Review billable records, update pricing, and send exports to Jonas
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
              {hasPermission("reload_billable_requests") && (
                <Button
                  variant="outlined"
                  onClick={fetchBillableRequests}
                  startIcon={<RefreshIcon />}
                  sx={secondaryButtonSx}
                >
                  Reload All
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={() => exportRequestsToCSV(requests, getMemberInfo, getTypeName)}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={secondaryButtonSx}
              >
                Export CSV
              </Button>

              {hasPermission("send_all_to_jonas") && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setConfirmOpen(true)}
                  sx={primaryButtonSx}
                >
                  Send All to Jonas
                </Button>
              )}
            </Box>
          </Box>

          {exportSummary && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "14px",
                border: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                Export Summary
              </Typography>
              <Typography sx={{ color: "#223142", mt: 0.5 }}>
                ✅ {exportSummary.successCount} records exported successfully.
              </Typography>
              {exportSummary.failed.length > 0 && (
                <Typography sx={{ color: "error.main", mt: 0.5 }}>
                  ❌ Failed to export: {exportSummary.failed.join(", ")}
                </Typography>
              )}
            </Paper>
          )}

          {viewMode === "cards" ? (
            <>
              <Grid container spacing={3}>
                {requests.map((req) => {
                  const member = getMemberInfo(req.member_number);

                  return (
                    <Grid item xs={12} md={6} lg={4} key={req.id}>
                      <Card
                        sx={{
                          borderRadius: "18px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                          backgroundColor: "rgba(255,255,255,0.98)",
                          height: "100%",
                        }}
                      >
                        <CardContent sx={{ p: 2.25 }}>
                          <Tabs
                            value={activeTab[req.id] || "details"}
                            onChange={(e, val) => handleTabChange(req.id, val)}
                            textColor="primary"
                            indicatorColor="primary"
                            sx={{
                              mb: 2,
                              minHeight: "40px",
                              "& .MuiTab-root": {
                                minHeight: "40px",
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "10px",
                              },
                            }}
                          >
                            <Tab
                              icon={<DescriptionOutlinedIcon fontSize="small" />}
                              iconPosition="start"
                              label="Details"
                              value="details"
                            />
                            {req.work_order_id && (
                              <Tab
                                icon={<CommentOutlinedIcon fontSize="small" />}
                                iconPosition="start"
                                label="Comments"
                                value="comments"
                              />
                            )}
                          </Tabs>

                          {activeTab[req.id] === "details" && (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 1.5,
                                  mb: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#223142",
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {req.title}
                                </Typography>

                                <Box
                                  sx={{
                                    minWidth: 120,
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: "12px",
                                    backgroundColor: "rgba(47,99,173,0.04)",
                                    border: "1px solid rgba(47,99,173,0.08)",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", color: "#6b778c" }}
                                  >
                                    Material: ${parseFloat(req.price || 0).toFixed(2)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", color: "#6b778c" }}
                                  >
                                    Labor: ${parseFloat(req.labor_price || 0).toFixed(2)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: "#223142", mt: 0.5 }}
                                  >
                                    Total: $
                                    {(
                                      parseFloat(req.price || 0) +
                                      parseFloat(req.labor_price || 0)
                                    ).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>

                              <Stack spacing={0.5}>
                                {req.member_number && (
                                  <Typography variant="body2" sx={{ color: "#223142" }}>
                                    <strong>Member #:</strong> {req.member_number}
                                  </Typography>
                                )}

                                {member && (
                                  <>
                                    <Typography variant="body2" sx={{ color: "#223142" }}>
                                      <strong>Name:</strong> {member.FirstName} {member.LastName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#223142" }}>
                                      <strong>Address:</strong> {member.FullAddress || "—"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#223142" }}>
                                      <strong>Membership Type:</strong>{" "}
                                      {member.ClubMembershipTypeCode || "—"}
                                    </Typography>
                                  </>
                                )}

                                {req.description && req.description !== req.title && (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#223142", pt: 0.5 }}
                                  >
                                    {req.description}
                                  </Typography>
                                )}
                              </Stack>

                              <Box sx={{ mt: 2 }}>
                                <Chip
                                  label={req.billable ? "Billable" : "Non-Billable"}
                                  size="small"
                                  sx={{
                                    borderRadius: "999px",
                                    fontWeight: 600,
                                    backgroundColor: req.billable
                                      ? "rgba(46,125,50,0.12)"
                                      : "rgba(0,0,0,0.06)",
                                    color: req.billable ? "success.dark" : "text.secondary",
                                  }}
                                />
                              </Box>

                              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                                <Typography variant="body2" sx={{ color: "#223142" }}>
                                  {getRequestTypeLabel(req)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#223142" }}>
                                  {getTypeName(req)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#223142" }}>
                                  Started: {new Date(req.started_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#223142" }}>
                                  Closed: {new Date(req.closed_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#223142" }}>
                                  Closed By: {req.closed_by}
                                </Typography>
                              </Stack>

                              <Box
                                sx={{
                                  mt: 2,
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: ["1", "2"].includes(String(req.work_order_type))
                                      ? "110px 90px 110px auto"
                                      : "110px 110px auto",
                                  },
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <TextField
                                  label="Material Price"
                                  size="small"
                                  type="number"
                                  value={req.price ?? 0}
                                  onChange={(e) =>
                                    handlePriceChange(req.id, e.target.value, "price")
                                  }
                                  sx={{ ...fieldSx }}
                                />

                                {["1", "2"].includes(String(req.work_order_type)) ? (
                                  <>
                                    <TextField
                                      label="Hours"
                                      size="small"
                                      type="number"
                                      inputProps={{ step: "0.01" }}
                                      value={req.hours ?? 0}
                                      onChange={(e) =>
                                        handleHoursChange(req.id, e.target.value)
                                      }
                                      sx={{ ...fieldSx }}
                                    />
                                    <TextField
                                      label="Labor Price"
                                      size="small"
                                      value={req.labor_price ?? 0}
                                      InputProps={{ readOnly: true }}
                                      sx={{ ...fieldSx }}
                                    />
                                  </>
                                ) : (
                                  <TextField
                                    label="Labor Price"
                                    size="small"
                                    type="number"
                                    value={req.labor_price ?? 0}
                                    onChange={(e) =>
                                      handlePriceChange(req.id, e.target.value, "labor_price")
                                    }
                                    sx={{ ...fieldSx }}
                                  />
                                )}

                                <Button
                                  variant="outlined"
                                  onClick={() => savePrice(req)}
                                  disabled={!req.billable}
                                  startIcon={<SaveOutlinedIcon />}
                                  sx={secondaryButtonSx}
                                >
                                  Save
                                </Button>
                              </Box>

                              {req.billable && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  {hasPermission("send_single_to_jonas") && req.billable && (
                                    <Button
                                      variant="contained"
                                      endIcon={<SendIcon />}
                                      onClick={() => handleSendToJonas(req.id)}
                                      sx={primaryButtonSx}
                                    >
                                      Send to Jonas
                                    </Button>
                                  )}

                                  {req.work_order_id && (
                                    <Button
                                      variant="outlined"
                                      component={Link}
                                      to={`/home/workorders/${req.work_order_id}`}
                                      startIcon={<VisibilityOutlinedIcon />}
                                      sx={secondaryButtonSx}
                                    >
                                      View Work Order
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </>
                          )}

                          {activeTab[req.id] === "comments" && (
                            <Box sx={{ mt: 1, maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                              {loadingComments[req.work_order_id] ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    py: 3,
                                  }}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                              ) : commentsMap[req.work_order_id]?.length ? (
                                commentsMap[req.work_order_id].map((c) => (
                                  <Box
                                    key={c.comment_id}
                                    sx={{
                                      mb: 1.25,
                                      p: 1.5,
                                      border: "1px solid rgba(47,99,173,0.08)",
                                      borderRadius: "12px",
                                      backgroundColor: c.is_internal
                                        ? "rgba(47,99,173,0.03)"
                                        : "#fff",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700, color: "#223142" }}
                                    >
                                      {c.user_name} {c.user_lastname}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: "pre-line", color: "#223142", mt: 0.5 }}
                                    >
                                      {c.comment_text}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#6b778c", mt: 0.75, display: "block" }}>
                                      {new Date(c.sent_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                                  No comments available.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                aria-labelledby="confirm-dialog"
                TransitionComponent={Slide}
                TransitionProps={{ direction: "up" }}
                PaperProps={{ sx: dialogPaperSx }}
              >
                <DialogTitle
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#223142",
                    pt: 3,
                  }}
                >
                  Confirm Export
                </DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                  <Typography sx={{ color: "#223142", textAlign: "center" }}>
                    Are you sure you want to send <strong>ALL</strong> billable requests to Jonas?
                  </Typography>
                </DialogContent>
                <DialogActions
                  sx={{
                    px: 3,
                    pb: 3,
                    justifyContent: "center",
                    gap: 1.25,
                  }}
                >
                  <Button onClick={() => setConfirmOpen(false)} variant="outlined" sx={secondaryButtonSx}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendAll}
                    variant="contained"
                    startIcon={<SendIcon />}
                    sx={primaryButtonSx}
                  >
                    Yes, Send All
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                maxWidth="md"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: "up" }}
                PaperProps={{ sx: dialogPaperSx }}
              >
                <DialogTitle
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#223142",
                    pt: 3,
                  }}
                >
                  Export Report
                </DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: "#223142", fontWeight: 600 }}>
                    The following records were sent to Jonas:
                  </Typography>

                  <Box
                    sx={{
                      maxHeight: 420,
                      overflow: "auto",
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "rgba(47,99,173,0.04)" }}>
                          <th style={{ padding: 12, textAlign: "left" }}>Member #</th>
                          <th style={{ padding: 12, textAlign: "left" }}>Invoice Date</th>
                          <th style={{ padding: 12, textAlign: "left" }}>Chit #</th>
                          <th style={{ padding: 12, textAlign: "left" }}>Item Group</th>
                          <th style={{ padding: 12, textAlign: "left" }}>Description</th>
                          <th style={{ padding: 12, textAlign: "right" }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exportedRecords.map((rec, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: 12 }}>{rec.member_number}</td>
                            <td style={{ padding: 12 }}>
                              {new Date(rec.invoice_date).toLocaleDateString()}
                            </td>
                            <td style={{ padding: 12 }}>{rec.chit_number}</td>
                            <td style={{ padding: 12 }}>{rec.item_group}</td>
                            <td style={{ padding: 12 }}>{rec.export_description}</td>
                            <td style={{ padding: 12, textAlign: "right" }}>
                              ${rec.total}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </DialogContent>
                <DialogActions
                  sx={{
                    px: 3,
                    pb: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button onClick={() => setExportModalOpen(false)} variant="outlined" sx={secondaryButtonSx}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <Box sx={{ width: "100%", mt: 1 }}>
              <DataGrid
                rows={requests}
                columns={columns}
                getRowId={(row) => row.id}
                processRowUpdate={handleRowUpdate}
                experimentalFeatures={{ newEditingApi: true }}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
                autoHeight
                sx={{
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "rgba(47,99,173,0.04)",
                    color: "#223142",
                    fontWeight: 700,
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                  },
                  "& .MuiDataGrid-row": {
                    borderBottom: "1px solid rgba(47,99,173,0.06)",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(47,99,173,0.03)",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "rgba(255,255,255,0.92)",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Integrations;